import { Link } from "react-router-dom";
import { extractConversationSignals } from "@/components/conversation/conversationListShared";
import { Skeleton } from "@/components/ui/skeleton";
import { ConsoleBand, VoiceBandData } from "@/types/dashboard/genie-console";
import { EmptyPreview, PreviewError } from "./previewShell";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface VoicePreviewProps {
  band: ConsoleBand<VoiceBandData>;
  brandColor: string;
  hasGenie: boolean;
}

/**
 * VOICE band preview — the user's ACTUAL conversations. Real summaries, real
 * topics, real timestamps, read through the same `extractConversationSignals`
 * helper the inbox list uses, so the wording stays consistent with the inbox.
 */
export const VoicePreview = ({ band, brandColor, hasGenie }: VoicePreviewProps) => {
  if (band.isError) return <PreviewError label="conversations" />;

  if (band.isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((row) => (
          <Skeleton key={row} className="h-14 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  const { conversations, unreviewedCount } = band.data;

  if (conversations.length === 0) {
    return (
      <EmptyPreview
        message={
          hasGenie
            ? "No conversations in the last 30 days. Once someone talks to your Genie it shows up here."
            : "Create a Genie and its conversations land here."
        }
        actionLabel={hasGenie ? "Open the inbox" : "Create a Genie"}
        to={hasGenie ? "/genie-hub/inbox" : "/user-hub/genie-setup"}
        brandColor={brandColor}
      />
    );
  }

  return (
    <div className="space-y-2">
      {conversations.map((conversation) => {
        const signals = extractConversationSignals(conversation);
        const isUnread = conversation.viewed !== true;

        return (
          <Link
            key={conversation.id}
            to={`/genie-hub/conversations/${conversation.id}`}
            className="flex items-start gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5 transition-colors hover:border-gray-200 hover:bg-[#F9FAFC]"
          >
            <span
              className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full"
              style={{ backgroundColor: isUnread ? brandColor : "#D1D5DB" }}
              aria-hidden="true"
            />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm text-gray-900">
                {signals.summaryPreview || signals.primaryTopic || "Conversation"}
              </span>
              <span className="block truncate text-xs text-gray-500">
                {signals.hasValidTime ? signals.timeAgo : "Recently"}
                {signals.durationSeconds
                  ? ` · ${Math.max(1, Math.round(signals.durationSeconds / 60))} min`
                  : ""}
                {signals.primaryTopic && signals.summaryPreview
                  ? ` · ${signals.primaryTopic}`
                  : ""}
              </span>
            </span>
          </Link>
        );
      })}

      {unreviewedCount > 0 && (
        <p className="pt-1 text-xs font-medium" style={{ color: brandColor }}>
          {unreviewedCount} not read yet
        </p>
      )}

      <GenieCoachingTips conversations={conversations} />
    </div>
  );
};

/**
 * Rolling coaching digest: the analysis engine attaches per-call
 * `recommendations` to every conversation — this surfaces the freshest few
 * on the dashboard so owners keep getting "how to improve your genie"
 * nudges without opening each call. Each tip links to its conversation.
 */
const GenieCoachingTips = ({
  conversations,
}: {
  conversations: VoiceBandData["conversations"];
}) => {
  const tips: { tip: string; conversationId: string }[] = [];
  const seen = new Set<string>();

  for (const conversation of conversations) {
    const recs = conversation.details?.analysis_result?.recommendations;
    if (!Array.isArray(recs)) continue;
    for (const rec of recs) {
      if (typeof rec !== "string" || rec.trim().length === 0) continue;
      const key = rec.trim().toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      tips.push({ tip: rec.trim(), conversationId: String(conversation.id) });
      if (tips.length >= 3) break;
    }
    if (tips.length >= 3) break;
  }

  if (tips.length === 0) return null;

  return (
    <div className="mt-3 rounded-xl border border-gray-100 bg-[#F9FAFC] px-3 py-2.5">
      <div className="mb-1.5 flex items-center gap-2">
        <GenieIcon name="lightbulb" size={16} className="h-4 w-4" />
        <span className="text-xs font-semibold text-gray-900">Make your genie better</span>
      </div>
      <ul className="space-y-1">
        {tips.map(({ tip, conversationId }) => (
          <li key={`${conversationId}-${tip.slice(0, 40)}`}>
            <Link
              to={`/genie-hub/conversations/${conversationId}`}
              className="block truncate text-xs text-gray-600 transition-colors hover:text-genie-primary"
            >
              {tip}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default VoicePreview;
